import React from 'react';

interface LayerConfig {
  key: string;
  label: string;
  color: string;
  /** Single-key toggle, shown on hover. Absent for layer sets with no shortcuts. */
  shortcut?: string;
}

interface LayersColumnProps {
  title: string;
  layer_config: LayerConfig[];
  enabledLayers: Set<string>;
  setEnabledLayers: (layers: Set<string>) => void;
  getLayerCount: (layerKey: string) => number;
  toggleLayer: (layerKey: string) => void;
}

export const LayersColumn: React.FC<LayersColumnProps> = ({
  title,
  layer_config,
  enabledLayers,
  setEnabledLayers,
  getLayerCount,
  toggleLayer,
}) => {
  return (
    <div className="lg:col-span-1 flex flex-col">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold">{title}</h3>
        <button
          onClick={() => {
            const allLayersEnabled = layer_config.every((layer) => enabledLayers.has(layer.key));
            const newSet = new Set<string>();

            if (!allLayersEnabled) {
              // Not all layers are enabled, so enable all
              layer_config.forEach((layer) => newSet.add(layer.key));
            }
            // If all layers are enabled, leave newSet empty to disable all

            setEnabledLayers(newSet);
          }}
          className="p-1.5 rounded-md transition-colors bg-gray-100 hover:bg-gray-200 text-gray-600"
          title={
            layer_config.every((layer) => enabledLayers.has(layer.key))
              ? 'Disable all layers'
              : 'Enable all layers'
          }
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            {layer_config.every((layer) => enabledLayers.has(layer.key)) ? (
              // "Uncheck all" icon - square with X
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            ) : (
              // "Check all" icon - checkmark
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 13l4 4L19 7"
              />
            )}
          </svg>
        </button>
      </div>
      <div className="space-y-2">
        {layer_config.map((layer) => {
          const count = getLayerCount(layer.key);
          const isEnabled = enabledLayers.has(layer.key);

          return (
            <button
              key={layer.key}
              onClick={() => toggleLayer(layer.key)}
              title={layer.label}
              className={`group w-full text-left p-3 rounded-md border transition-colors ${
                isEnabled
                  ? 'border-blue-300 bg-blue-50 text-blue-900'
                  : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50'
              }`}
            >
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center min-w-0">
                  <div className={`w-3 h-3 rounded-full ${layer.color} mr-2 shrink-0`}></div>
                  <span className="text-sm font-medium truncate">{layer.label}</span>
                  {layer.shortcut && (
                    /* invisible rather than hidden, so revealing it never shifts the row */
                    <kbd className="ml-2 shrink-0 invisible group-hover:visible text-[10px] font-mono font-semibold bg-gray-50 border border-gray-300 border-b-2 text-gray-500 px-1.5 py-0.5 rounded">
                      {layer.shortcut.toUpperCase()}
                    </kbd>
                  )}
                </div>
                <span className="text-xs bg-gray-200 text-gray-700 px-2 py-1 rounded shrink-0">
                  {count}
                </span>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default LayersColumn;
